import { AdvancedFilterModel, ColDef, Column, ColumnAdvancedFilterModel, GridApi } from 'ag-grid-community';
import { addDays } from 'date-fns/addDays';
import { format } from 'date-fns/format';
import { parse } from 'date-fns/parse';

export type GridFilterDataType = 'text' | 'number' | 'date' | 'dateString' | 'boolean' | 'set';

export type GridFilterSetValue = string | number | boolean;

export interface IGridFilterOption {
  value: GridFilterSetValue;
  label: string;
}

export interface IGridFilterColumn {
  colId: string;
  header: string;
  dataType: GridFilterDataType;
  /** Displayed decimal places of a number column; conditions compare at this precision. */
  decimals?: number;
  /** Selectable values of a set column. */
  options?: IGridFilterOption[];
}

export interface IGridFilterCondition {
  colId?: string;
  operator?: string;
  value?: string;
  /** Selected values of an in/notIn condition on a set column. */
  values?: GridFilterSetValue[];
  joinWithPrevious?: 'AND' | 'OR';
}

export interface IGridFilterState {
  text?: string;
  conditions?: IGridFilterCondition[];
}

export interface IGridFilterOperator {
  value: string;
  label: string;
}

const textOperators: IGridFilterOperator[] = [
  { value: 'contains', label: 'Contains' },
  { value: 'notContains', label: 'Does not contain' },
  { value: 'equals', label: 'Equals' },
  { value: 'notEqual', label: 'Not equal' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
  { value: 'blank', label: 'Is blank' },
  { value: 'notBlank', label: 'Is not blank' }
];

const scalarOperators: IGridFilterOperator[] = [
  { value: 'equals', label: '=' },
  { value: 'notEqual', label: '!=' },
  { value: 'greaterThan', label: '>' },
  { value: 'greaterThanOrEqual', label: '>=' },
  { value: 'lessThan', label: '<' },
  { value: 'lessThanOrEqual', label: '<=' },
  { value: 'blank', label: 'Is blank' },
  { value: 'notBlank', label: 'Is not blank' }
];

const booleanOperators: IGridFilterOperator[] = [
  { value: 'true', label: 'Is true' },
  { value: 'false', label: 'Is false' }
];

const setOperators: IGridFilterOperator[] = [
  { value: 'in', label: 'Is one of' },
  { value: 'notIn', label: 'Is not one of' }
];

export function operatorsForDataType(dataType: GridFilterDataType): IGridFilterOperator[] {
  switch (dataType) {
    case 'number':
    case 'date':
    case 'dateString':
      return scalarOperators;
    case 'boolean':
      return booleanOperators;
    case 'set':
      return setOperators;
    default:
      return textOperators;
  }
}

export function isSetOperator(operator: string): boolean {
  return operator === 'in' || operator === 'notIn';
}

function parseNumberValue(value: string): number {
  // the last . or , is the decimal separator; every other separator is thousands grouping
  const trimmed = value.replace(/\s/g, '');
  const decimalIndex = Math.max(trimmed.lastIndexOf(','), trimmed.lastIndexOf('.'));
  return Number(trimmed.split('').map((ch, i) => /[.,]/.test(ch) ? (i === decimalIndex ? '.' : '') : ch).join(''));
}

export function operatorNeedsValue(operator: string): boolean {
  return !['blank', 'notBlank', 'true', 'false', undefined].includes(operator);
}

export function isConditionComplete(condition: IGridFilterCondition): boolean {
  if (!condition.colId || !condition.operator) {
    return false;
  }
  if (isSetOperator(condition.operator)) {
    return !!condition.values?.length;
  }
  return !operatorNeedsValue(condition.operator) || !!condition.value;
}

export function describeCondition(condition: IGridFilterCondition, columns: IGridFilterColumn[]): string {
  const column = columns.find(x => x.colId === condition.colId);
  const operator = column
    ? operatorsForDataType(column.dataType).find(x => x.value === condition.operator)
    : undefined;
  let value = operatorNeedsValue(condition.operator) ? condition.value : undefined;
  if (isSetOperator(condition.operator)) {
    value = (condition.values ?? [])
      .map(x => column?.options?.find(o => o.value === x)?.label ?? String(x))
      .join(', ');
  } else if (value && isDateDataType(column?.dataType)) {
    const date = new Date(normalizeDateValue(value));
    if (!isNaN(date.getTime())) {
      value = format(date, 'dd.MM.yyyy');
    }
  }
  return [
    column?.header ?? condition.colId,
    operator?.label ?? condition.operator,
    value
  ].filter(x => !!x).join(' ');
}

function isDateDataType(dataType: GridFilterDataType | undefined): boolean {
  return dataType === 'date' || dataType === 'dateString';
}

function normalizeDateValue(value: string): string {
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
    return format(parse(value, 'dd.MM.yyyy', new Date()), 'yyyy-MM-dd');
  }
  return value.substring(0, 10);
}

export function getGridFilterColumns(gridApi: GridApi): IGridFilterColumn[] {
  return (gridApi.getColumns() ?? [])
    .map(column => ({ column, header: gridApi.getDisplayNameForColumn(column, null) }))
    .filter(x => !!x.header)
    .map(x => {
      const colDef = x.column.getColDef();
      const dataType = getDataType(colDef);
      return {
        colId: x.column.getColId(),
        header: x.header,
        dataType,
        decimals: getDecimals(colDef),
        options: dataType === 'set' ? getSetOptions(gridApi, x.column, colDef) : undefined
      };
    });
}

function getSetOptions(gridApi: GridApi, column: Column, colDef: ColDef): IGridFilterOption[] {
  const formatter = (colDef.filterParams as { valueFormatter?: (params: { value: unknown }) => string } | undefined)?.valueFormatter
    ?? (typeof colDef.valueFormatter === 'function' ? colDef.valueFormatter : undefined);
  const options = new Map<GridFilterSetValue, string>();
  const explicitValues = (colDef.filterParams as { values?: GridFilterSetValue[] } | undefined)?.values;
  if (Array.isArray(explicitValues)) {
    for (const value of explicitValues) {
      options.set(value, String((formatter as (params: { value: unknown }) => string)?.({ value }) ?? value));
    }
  } else {
    gridApi.forEachLeafNode(rowNode => {
      const value = gridApi.getCellValue({ rowNode, colKey: column }) as GridFilterSetValue;
      if (value != null && !options.has(value)) {
        options.set(value, String(gridApi.getCellValue({ rowNode, colKey: column, useFormatter: true }) ?? value));
      }
    });
  }
  return [...options.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

// displayed decimal places of the wrapper's numeric column types (see the valueFormatters
// in ag-grid-aurelia.ts); conditions compare at this precision
function getDecimals(colDef: ColDef): number | undefined {
  const types = typeof colDef.type === 'string' ? [colDef.type] : colDef.type ?? [];
  if (types.includes('amountOneDec')) return 1;
  if (types.includes('amountTwoDec')) return 2;
  if (types.includes('amount') || types.includes('amountFourDec')) return 4;
  if (types.includes('price')) return 6;
  return undefined;
}

function getDataType(colDef: ColDef): GridFilterDataType {
  if (colDef.filter === 'agSetColumnFilter') {
    return 'set';
  }
  // the declared column type wins: date columns hold ISO strings and would otherwise be
  // (mis)classified by the grid's inferred cellDataType
  const types = typeof colDef.type === 'string' ? [colDef.type] : colDef.type ?? [];
  if (types.some(t => t === 'date' || t === 'datetime')) {
    return 'dateString';
  }
  if (types.some(t => t === 'number' || t === 'price' || t.startsWith('amount') || t === 'numericColumn' || t === 'rightAligned')) {
    return 'number';
  }
  if (typeof colDef.cellDataType === 'string') {
    switch (colDef.cellDataType) {
      case 'number': return 'number';
      case 'date': return 'date';
      case 'dateString': return 'dateString';
      case 'boolean': return 'boolean';
    }
  }
  return 'text';
}

export function buildAdvancedFilterModel(conditions: IGridFilterCondition[], columns: IGridFilterColumn[]): AdvancedFilterModel | null {
  const complete = conditions.filter(isConditionComplete);
  if (!complete.length) {
    return null;
  }

  // AND binds tighter than OR: runs of AND-joined conditions become nested AND joins under an OR root
  const groups: AdvancedFilterModel[][] = [[]];
  for (const condition of complete) {
    if (groups[groups.length - 1].length && condition.joinWithPrevious === 'OR') {
      groups.push([]);
    }
    groups[groups.length - 1].push(toConditionModel(condition, columns));
  }

  const joined = groups.map(group => group.length === 1
    ? group[0]
    : { filterType: 'join' as const, type: 'AND' as const, conditions: group });
  return joined.length === 1
    ? joined[0]
    : { filterType: 'join', type: 'OR', conditions: joined };
}

function toConditionModel(condition: IGridFilterCondition, columns: IGridFilterColumn[]): AdvancedFilterModel {
  const column = columns.find(x => x.colId === condition.colId);
  const dataType = column?.dataType ?? 'text';
  if (isSetOperator(condition.operator)) {
    return toSetModel(condition);
  }
  if (isDateDataType(dataType) && operatorNeedsValue(condition.operator)) {
    return toDateModel(condition, dataType);
  }
  if (dataType === 'number' && column?.decimals != null && operatorNeedsValue(condition.operator)) {
    return toNumberModel(condition, column.decimals);
  }
  const model = { filterType: dataType, colId: condition.colId, type: condition.operator } as unknown as ColumnAdvancedFilterModel;
  if (operatorNeedsValue(condition.operator)) {
    (model as { filter?: string | number }).filter = dataType === 'number' ? parseNumberValue(condition.value) : condition.value;
  }
  return model;
}

// the advanced filter has no set/in operator, so a value selection is expanded into
// equality comparisons joined with OR (or negated equalities joined with AND)
function toSetModel(condition: IGridFilterCondition): AdvancedFilterModel {
  const negate = condition.operator === 'notIn';
  const nodes = (condition.values ?? []).map(value => toSetValueNode(condition.colId, value, negate));
  return nodes.length === 1
    ? nodes[0]
    : { filterType: 'join', type: negate ? 'AND' : 'OR', conditions: nodes };
}

function toSetValueNode(colId: string, value: GridFilterSetValue, negate: boolean): ColumnAdvancedFilterModel {
  if (typeof value === 'boolean') {
    return { filterType: 'boolean', colId, type: String(negate ? !value : value) } as unknown as ColumnAdvancedFilterModel;
  }
  return {
    filterType: typeof value === 'number' ? 'number' : 'text',
    colId,
    type: negate ? 'notEqual' : 'equals',
    filter: value
  } as unknown as ColumnAdvancedFilterModel;
}

// cell values may carry more precision than the column displays, so comparisons at the
// displayed precision are expressed as [value - half, value + half) ranges
function toNumberModel(condition: IGridFilterCondition, decimals: number): AdvancedFilterModel {
  const value = parseNumberValue(condition.value);
  const half = 0.5 / Math.pow(10, decimals);
  const node = (type: string, filter: number) =>
    ({ filterType: 'number', colId: condition.colId, type, filter } as unknown as ColumnAdvancedFilterModel);
  switch (condition.operator) {
    case 'equals':
      return { filterType: 'join', type: 'AND', conditions: [node('greaterThanOrEqual', value - half), node('lessThan', value + half)] };
    case 'notEqual':
      return { filterType: 'join', type: 'OR', conditions: [node('lessThan', value - half), node('greaterThanOrEqual', value + half)] };
    case 'greaterThan':
      return node('greaterThanOrEqual', value + half);
    case 'greaterThanOrEqual':
      return node('greaterThanOrEqual', value - half);
    case 'lessThan':
      return node('lessThan', value - half);
    case 'lessThanOrEqual':
      return node('lessThan', value + half);
    default:
      return node(condition.operator, value);
  }
}

// cell values may carry a time component while the condition value is a whole day,
// so day comparisons are expressed as [day, nextDay) ranges
function toDateModel(condition: IGridFilterCondition, dataType: GridFilterDataType): AdvancedFilterModel {
  const day = normalizeDateValue(condition.value);
  const nextDay = format(addDays(parse(day, 'yyyy-MM-dd', new Date()), 1), 'yyyy-MM-dd');
  const node = (type: string, filter: string) =>
    ({ filterType: dataType, colId: condition.colId, type, filter } as unknown as ColumnAdvancedFilterModel);
  switch (condition.operator) {
    case 'equals':
      return { filterType: 'join', type: 'AND', conditions: [node('greaterThanOrEqual', day), node('lessThan', nextDay)] };
    case 'notEqual':
      return { filterType: 'join', type: 'OR', conditions: [node('lessThan', day), node('greaterThanOrEqual', nextDay)] };
    case 'greaterThan':
      return node('greaterThanOrEqual', nextDay);
    case 'lessThanOrEqual':
      return node('lessThan', nextDay);
    default:
      return node(condition.operator, day);
  }
}
